import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { formSettings } from './../settings';
import { FormattedMessage } from 'react-intl';
import WizardButtons from '../../../../../../components/wizard/navigator/buttons';
import Signature from '../../../../../../components/signature';
import messages from '../../../../../../text/messages';
import { setProgressionProperties, getOfferLetter } from '../../../../../../reducers/onboarding';
import { maskSSN } from '../../../../../../utilities/string';
import { getAddresses } from '../../../../../../reducers/addresses';
import { getSignatures, createOfferLetter } from '../../../../../../reducers/documents';
import { sign } from '../../../../../../reducers/signatures';
import Document from './../../../../../../components/document';
import { CheckboxField } from './../../../../../../components/fields';
import { updateUserProfile } from '../../../../../../reducers/identity';
import { DocumentInfo, PERMISSIONS } from '../../../../../../utilities/documentTypes';
import { Signatures } from '../../../../../../utilities/signatureTypes';
import mixpanel from 'mixpanel-browser';

class OfferLetterStep extends React.Component {
    constructor(props) {
        super(props);

        this.submit = this.submit.bind(this);
    }

    componentDidMount() {
        mixpanel.track('WorkerOnboarding', { _data: { branch: 'employee', step: 'OfferLetterStep' } });

        const { role, profile, job, progression, offerLetter, getAddresses, getSignatures, setProgressionProperties, getOfferLetter } = this.props;

        if (!offerLetter) {
            getOfferLetter(job.id, role.id, role.type, 'offer-letter');
        }

        getSignatures(role, signaturesResult =>
            setProgressionProperties({
                offerLetterSignature: signaturesResult.find(s => s.name === `${Signatures.types.OFFER_LETTER.name}:${job.id}`) !== undefined
            })
        );

        if (!progression || !progression.personalInformation || !progression.mailingAddress) {
            getAddresses(role, addresses => {
                setProgressionProperties({
                    personalInformation: {
                        firstName: profile.first_name,
                        middleName: profile.middle_name,
                        lastName: profile.last_name,
                        email: profile.email,
                        phoneNumber: profile.phone,
                        dateOfBirth: profile.birthday_date,
                        socialSecurityNumber: maskSSN(profile.ssn_bg_check)
                    }
                });

                if (addresses && addresses.length > 0) {
                    setProgressionProperties({ mailingAddress: addresses[0] });
                }
            });
        }
    }

    submit(values, action) {
        const { role, job, sign, setProgressionProperties, profile, updateUserProfile, createOfferLetter } = this.props;
        updateUserProfile({ ...profile, inventions_disclosed: values.inventions_disclosed }, () => {
            sign(role, `${Signatures.types.OFFER_LETTER.name}:${job.id}`, signature => {
                setProgressionProperties({ offerLetterSignature: true });
                createOfferLetter(job.id, job.contractor_id, signature.id);
                action();
            });
        });
    }

    render() {
        const {
            handleSubmit,
            onSubmit,
            back,
            submitting,
            job,
            progression,
            address,
            admin,
            isInventionsDisclosed,
            inventionsDisclosureDocument,
            workerAddress,
            offerLetter
        } = this.props;
        if (!progression || !progression.personalInformation || !progression.mailingAddress || progression.offerLetterSignature === undefined || !offerLetter) {
            return null;
        }

        const { ADMIN_CLC_SECTION, CONTRACTOR_INVENTION_DISCLOSURE } = DocumentInfo.types;

        const isCA =
            (job.work_location_type === 'onsite' && address && address.state === 'CA') ||
            (job.work_location_type === 'offsite' && workerAddress && workerAddress.state === 'CA');

        return (
            <form onSubmit={handleSubmit(values => this.submit(values, onSubmit))}>
                <div className="row">
                    <div className="col-xs-12">
                        <FormattedMessage
                            id={'offerLetter.intro1'}
                            defaultMessage={
                                'There are two documents that detail how you will be employed.  \
                                 The first, this Offer Letter, includes general terms and conditions. \
                                 The second, on the next page, specifies the details of your work for the client.'
                            }
                        />
                    </div>
                </div>
                <div className="row section">
                    <div className="col-xs-12">
                        <FormattedMessage
                            id={'offerLetter.intro1'}
                            defaultMessage={
                                'Please review and e-sign both documents. \
                                 You can download copies from your dashboard if you ever need to.'
                            }
                        />
                    </div>
                </div>
                <div className="row section">
                    <div className="col-xs-12">
                        <div className="scrolled" dangerouslySetInnerHTML={{ __html: offerLetter }} />
                    </div>
                </div>
                {isCA && (
                    <div className="row">
                        <div className="col-xs-12">
                            <Document
                                {...ADMIN_CLC_SECTION}
                                view={{ showHeader: false, component: 'icon-download' }}
                                ownerRole={{ id: admin.id, type: 'admin' }}
                            />
                        </div>
                    </div>
                )}
                <Field
                    name={'inventions_disclosed'}
                    checked={isInventionsDisclosed}
                    placeholder={{ id: 'offerLetter.inventionsDisclosed', defaultMessage: 'I have invention(s) to disclose' }}
                    component={CheckboxField}
                />
                {isInventionsDisclosed && (
                    <div>
                        <div className="row group">
                            <div className="col-xs-12">
                                <Document
                                    {...CONTRACTOR_INVENTION_DISCLOSURE}
                                    upload={{ limit: 1, permissions: PERMISSIONS.DEFAULT }}
                                    view={{ showHeader: false, component: 'icon-download' }}
                                />
                            </div>
                        </div>
                        <div className="row group">
                            <div className="col-xs-12">
                                <FormattedMessage
                                    id={'offerLetter.inventionsDisclosedDescription'}
                                    defaultMessage={'For each disclosed invention, please indicate the name, date of invention, owner, and a description.'}
                                />
                            </div>
                        </div>
                    </div>
                )}
                <div className="row">
                    <div className="col-xs-12">
                        <Signature name={`${Signatures.types.OFFER_LETTER.name}:${job.id}`} />
                    </div>
                </div>
                <WizardButtons
                    disabled={submitting || (isInventionsDisclosed === true && !inventionsDisclosureDocument.list.length)}
                    back={{ action: back }}
                    submit={{ message: <FormattedMessage {...messages.signAndContinue} /> }}
                />
            </form>
        );
    }
}

OfferLetterStep = reduxForm(formSettings)(OfferLetterStep);
const selector = formValueSelector(formSettings.form);
const mapStateToProps = state => {
    const progression = state.onboarding.progression;
    const profile = state.identity.profile;
    const workerAddresses = state.addresses.list || [{}];
    const inventionDocType = DocumentInfo.types.CONTRACTOR_INVENTION_DISCLOSURE.docType;
    return {
        role: state.identity.role,
        profile,
        job: state.onboarding.job,
        client: state.onboarding.client,
        admin: state.users.adminDetails,
        position: state.onboarding.position,
        offerLetter: state.onboarding.offerLetter,
        isInventionsDisclosed: selector(state, 'inventions_disclosed') === true,
        workerAddress: workerAddresses[workerAddresses.length - 1],
        progression,
        inventionsDisclosureDocument: state.documents[inventionDocType] || { list: [] },
        initialValues: {
            acknowledgeOfferLetterSignature: progression.offerLetterSignature,
            inventions_disclosed: profile.inventions_disclosed
        }
    };
};

const mapDispatchToProps = dispatch =>
    bindActionCreators(
        {
            getAddresses,
            getSignatures,
            createOfferLetter,
            getOfferLetter,
            sign,
            setProgressionProperties,
            updateUserProfile
        },
        dispatch
    );

export default connect(mapStateToProps, mapDispatchToProps)(OfferLetterStep);



// WEBPACK FOOTER //
// ./src/pages/onboarding/tenants/contractor/steps/employee/offerLetter.jsx